// src/components/BankingManagement.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, deleteDoc, serverTimestamp, getDoc, getDocs } from 'firebase/firestore';

const BankingManagement = ({ db, schoolId, userRole, setMessage, setError }) => {
  const [activeTab, setActiveTab] = useState('accounts'); // 'accounts', 'transactions', 'reconciliation'
  const [bankAccounts, setBankAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [newAccountName, setNewAccountName] = useState('');
  const [newAccountBalance, setNewAccountBalance] = useState(''); // Initialisé à vide pour la validation
  const [newTransactionType, setNewTransactionType] = useState('deposit');
  const [newTransactionAmount, setNewTransactionAmount] = useState('');
  const [newTransactionDescription, setNewTransactionDescription] = useState('');
  const [newTransactionAccountId, setNewTransactionAccountId] = useState('');
  const [transferTargetAccountId, setTransferTargetAccountId] = useState('');
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);

  // Pour la modification de transaction
  const [editingTransaction, setEditingTransaction] = useState(null); // { id: ..., data: {...} }

  // Pour la recherche et le filtre
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  // Pour le rapprochement bancaire
  const [reconciliationAccount, setReconciliationAccount] = useState('');
  const [bankStatementBalance, setBankStatementBalance] = useState('');
  const [unreconciledTransactions, setUnreconciledTransactions] = useState([]);
  const [reconciliationDate, setReconciliationDate] = useState('');

  // --- Chargement des comptes bancaires ---
  useEffect(() => {
    if (!db || !schoolId) {
      setIsLoadingAccounts(false);
      return;
    }

    setIsLoadingAccounts(true);
    const q = query(
      collection(db, 'bankAccounts'),
      where('schoolId', '==', schoolId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const accountsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBankAccounts(accountsData);
      setIsLoadingAccounts(false);
      setMessage('Comptes bancaires chargés.');
    }, (error) => {
      setError(`Erreur lors du chargement des comptes: ${error.message}`);
      setMessage('');
      setIsLoadingAccounts(false);
    });

    return () => unsubscribe();
  }, [db, schoolId, setMessage, setError]);

  // --- Chargement et filtrage des transactions pour le compte sélectionné ---
  const fetchTransactions = useCallback(async () => {
    if (!db || !schoolId || !selectedAccountId) {
      setTransactions([]);
      setUnreconciledTransactions([]);
      setIsLoadingTransactions(false);
      return;
    }

    setIsLoadingTransactions(true);
    let q = query(
      collection(db, 'bankTransactions'),
      where('schoolId', '==', schoolId),
      where('accountId', '==', selectedAccountId)
    );

    // Appliquer les filtres de recherche et de type
    if (filterType !== 'all') {
      q = query(q, where('type', '==', filterType));
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      let transactionsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Filtrer par terme de recherche
      if (searchTerm.trim()) {
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        transactionsData = transactionsData.filter(transaction =>
          transaction.description.toLowerCase().includes(lowerCaseSearchTerm)
        );
      }

      // Filtrer par plage de dates
      if (filterStartDate) {
        const start = new Date(filterStartDate);
        transactionsData = transactionsData.filter(transaction =>
          transaction.timestamp?.toDate() >= start
        );
      }
      if (filterEndDate) {
        const end = new Date(filterEndDate);
        end.setHours(23, 59, 59, 999); // Inclure toute la journée de fin
        transactionsData = transactionsData.filter(transaction =>
          transaction.timestamp?.toDate() <= end
        );
      }

      // Trier les transactions par date (du plus récent au plus ancien)
      transactionsData.sort((a, b) => (b.timestamp?.toDate() || 0) - (a.timestamp?.toDate() || 0));

      setTransactions(transactionsData);
      setUnreconciledTransactions(transactionsData.filter(t => !t.reconciled)); // Pour le rapprochement
      setIsLoadingTransactions(false);
      setMessage(`Transactions chargées pour le compte ${bankAccounts.find(acc => acc.id === selectedAccountId)?.name || selectedAccountId}.`);
    }, (error) => {
      setError(`Erreur lors du chargement des transactions: ${error.message}`);
      setMessage('');
      setIsLoadingTransactions(false);
    });

    return () => unsubscribe();
  }, [db, schoolId, selectedAccountId, filterType, searchTerm, filterStartDate, filterEndDate, setMessage, setError, bankAccounts]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // --- Gestion de l'ajout de compte bancaire ---
  const handleAddAccount = async (e) => {
    e.preventDefault();
    if (!newAccountName.trim()) {
      setError('Le nom du compte est obligatoire.');
      return;
    }
    const initialBalance = parseFloat(newAccountBalance);
    if (isNaN(initialBalance)) {
      setError('Veuillez fournir un solde initial valide.');
      return;
    }

    try {
      await addDoc(collection(db, 'bankAccounts'), {
        schoolId: schoolId,
        name: newAccountName.trim(),
        balance: initialBalance,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setMessage('Compte bancaire ajouté avec succès !');
      setNewAccountName('');
      setNewAccountBalance('');
      setError('');
    } catch (error) {
      setError(`Erreur lors de l'ajout du compte: ${error.message}`);
      setMessage('');
    }
  };

  // --- Gestion de la suppression de compte bancaire ---
  const handleDeleteAccount = async (accountId) => {
    if (userRole !== 'admin') {
      setError('Vous n\'êtes pas autorisé à supprimer des comptes.');
      return;
    }
    const accountName = bankAccounts.find(acc => acc.id === accountId)?.name;
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le compte "${accountName}" et toutes ses transactions associées ? Cette action est irréversible.`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'bankAccounts', accountId));

      const transactionsToDeleteQuery = query(
        collection(db, 'bankTransactions'),
        where('accountId', '==', accountId)
      );
      const querySnapshot = await getDocs(transactionsToDeleteQuery);
      const deletePromises = [];
      querySnapshot.forEach((d) => {
        deletePromises.push(deleteDoc(doc(db, 'bankTransactions', d.id)));
      });
      await Promise.all(deletePromises);

      setMessage('Compte et transactions associées supprimés avec succès !');
      if (selectedAccountId === accountId) {
        setSelectedAccountId('');
      }
      setError('');
    } catch (error) {
      setError(`Erreur lors de la suppression du compte: ${error.message}`);
      setMessage('');
    }
  };

  // --- Gestion de l'ajout/modification de transaction ---
  const handleAddEditTransaction = async (e) => {
    e.preventDefault();
    if (!newTransactionAccountId) {
      setError('Veuillez sélectionner un compte.');
      return;
    }
    const transactionAmount = parseFloat(newTransactionAmount);
    if (isNaN(transactionAmount) || transactionAmount <= 0) {
      setError('Veuillez saisir un montant valide et positif.');
      return;
    }
    if (newTransactionType === 'transfer' && (!transferTargetAccountId || newTransactionAccountId === transferTargetAccountId)) {
      setError('Pour un virement, veuillez sélectionner un compte cible différent du compte source.');
      return;
    }

    try {
      const accountRef = doc(db, 'bankAccounts', newTransactionAccountId);
      const accountDoc = await getDoc(accountRef);
      if (!accountDoc.exists()) {
        setError('Compte bancaire source introuvable.');
        return;
      }
      let currentBalance = accountDoc.data().balance;
      let newBalance = currentBalance;
      const amountToProcess = transactionAmount;

      if (editingTransaction) {
        // Logique de modification de transaction
        const oldTransaction = editingTransaction.data;
        const oldAmount = oldTransaction.amount;
        const oldType = oldTransaction.type;
        const oldAccountId = oldTransaction.accountId; // Le compte de l'ancienne transaction

        // Revert old transaction effect on balance (if modifying same account)
        const oldAccountRef = doc(db, 'bankAccounts', oldAccountId);
        const oldAccountDoc = await getDoc(oldAccountRef);
        if (oldAccountDoc.exists()) {
          let oldAccountBalance = oldAccountDoc.data().balance;
          if (oldType === 'deposit') {
            oldAccountBalance -= oldAmount;
          } else if (oldType === 'withdrawal') {
            oldAccountBalance += oldAmount;
          } else if (oldType === 'transfer') {
             oldAccountBalance += oldAmount; // Source gets amount back
             // Find the corresponding target transaction (simplistic, could be complex)
             const targetTransferQ = query(
                collection(db, 'bankTransactions'),
                where('timestamp', '==', oldTransaction.timestamp), // This is a weak link, better to store targetTxId
                where('amount', '==', oldAmount),
                where('type', '==', 'deposit'),
                where('description', '==', `Virement reçu de ${bankAccounts.find(acc => acc.id === oldAccountId)?.name || 'un autre compte'}${oldTransaction.description ? ' - ' + oldTransaction.description : ''}`),
                // Add more filters to uniquely identify the target transaction if possible
             );
             const targetTransferSnapshot = await getDocs(targetTransferQ);
             if (!targetTransferSnapshot.empty) {
                 const targetTxDoc = targetTransferSnapshot.docs[0];
                 const targetTxAccountId = targetTxDoc.data().accountId;
                 const targetAccountRef = doc(db, 'bankAccounts', targetTxAccountId);
                 const targetAccountDoc = await getDoc(targetAccountRef);
                 if (targetAccountDoc.exists()) {
                    await updateDoc(targetAccountRef, { balance: targetAccountDoc.data().balance - oldAmount });
                    await deleteDoc(doc(db, 'bankTransactions', targetTxDoc.id)); // Delete target side of transfer
                 }
             }
          }
          await updateDoc(oldAccountRef, { balance: oldAccountBalance });
        }


        // Apply new transaction effect on balance (if modifying same account, this will be handled below)
        // If account changed, new account balance needs to be adjusted
        if (oldAccountId !== newTransactionAccountId) {
            // New account is already handled by `accountRef` and `newBalance` calculation below.
            // No specific action needed here for the "new" account's old balance.
        }

        // Now, proceed with applying the new transaction's effect on the newTransactionAccountId
        // (which might be the same as oldAccountId)
        if (newTransactionType === 'deposit') {
          newBalance += amountToProcess;
        } else if (newTransactionType === 'withdrawal') {
          if (newBalance < amountToProcess && userRole !== 'admin') {
            throw new Error('Solde insuffisant pour ce retrait après ajustement.');
          }
          newBalance -= amountToProcess;
        } else if (newTransactionType === 'transfer') {
          const targetAccountRef = doc(db, 'bankAccounts', transferTargetAccountId);
          const targetAccountDoc = await getDoc(targetAccountRef);
          if (!targetAccountDoc.exists()) {
            throw new Error('Compte cible du virement introuvable.');
          }
          let targetBalance = targetAccountDoc.data().balance;
          newBalance -= amountToProcess;
          await updateDoc(targetAccountRef, { balance: targetBalance + amountToProcess });

          // Add a new target side transaction for the updated transfer
          await addDoc(collection(db, 'bankTransactions'), {
            schoolId: schoolId,
            accountId: transferTargetAccountId,
            type: 'deposit',
            amount: amountToProcess,
            description: `Virement reçu de ${bankAccounts.find(acc => acc.id === newTransactionAccountId)?.name || 'un autre compte'}${newTransactionDescription ? ' - ' + newTransactionDescription : ''}`,
            timestamp: serverTimestamp(),
          });
        }

        await updateDoc(accountRef, { balance: newBalance }); // Update the current account
        await updateDoc(doc(db, 'bankTransactions', editingTransaction.id), {
          accountId: newTransactionAccountId,
          type: newTransactionType,
          amount: amountToProcess,
          description: newTransactionDescription,
          updatedAt: serverTimestamp(),
        });
        setMessage('Transaction modifiée avec succès !');

      } else {
        // Logique d'ajout de nouvelle transaction
        if (newTransactionType === 'deposit') {
          newBalance += amountToProcess;
        } else if (newTransactionType === 'withdrawal') {
          if (currentBalance < amountToProcess && userRole !== 'admin') {
            setError('Solde insuffisant pour ce retrait.');
            return;
          }
          newBalance -= amountToProcess;
        } else if (newTransactionType === 'transfer') {
          const targetAccountRef = doc(db, 'bankAccounts', transferTargetAccountId);
          const targetAccountDoc = await getDoc(targetAccountRef);
          if (!targetAccountDoc.exists()) {
            setError('Compte cible du virement introuvable.');
            return;
          }
          let targetBalance = targetAccountDoc.data().balance;
          newBalance -= amountToProcess;
          await updateDoc(targetAccountRef, { balance: targetBalance + amountToProcess });

          await addDoc(collection(db, 'bankTransactions'), {
            schoolId: schoolId,
            accountId: transferTargetAccountId,
            type: 'deposit',
            amount: amountToProcess,
            description: `Virement reçu de ${bankAccounts.find(acc => acc.id === newTransactionAccountId)?.name || 'un autre compte'}${newTransactionDescription ? ' - ' + newTransactionDescription : ''}`,
            timestamp: serverTimestamp(),
          });
        }

        await updateDoc(accountRef, { balance: newBalance });

        await addDoc(collection(db, 'bankTransactions'), {
          schoolId: schoolId,
          accountId: newTransactionAccountId,
          type: newTransactionType,
          amount: amountToProcess,
          description: newTransactionDescription,
          timestamp: serverTimestamp(),
        });
        setMessage('Transaction enregistrée et solde(s) du compte mis à jour !');
      }

      // Reset form fields
      setNewTransactionAmount('');
      setNewTransactionDescription('');
      setNewTransactionType('deposit');
      setTransferTargetAccountId('');
      setEditingTransaction(null); // Clear editing state
      setError('');
    } catch (error) {
      setError(`Erreur lors de l'enregistrement de la transaction: ${error.message}`);
      setMessage('');
    }
  };

  // --- Préparer la modification d'une transaction ---
  const handleEditClick = (transaction) => {
    setEditingTransaction({ id: transaction.id, data: transaction });
    setNewTransactionAccountId(transaction.accountId);
    setNewTransactionType(transaction.type);
    setNewTransactionAmount(transaction.amount);
    setNewTransactionDescription(transaction.description);
    if (transaction.type === 'transfer') {
        // This is complex: for transfers, we need to find the target account.
        // A robust solution would involve storing the targetAccountId in the source transaction.
        // For simplicity, we'll leave transferTargetAccountId empty for edits, user will re-select.
        setTransferTargetAccountId('');
    }
    setActiveTab('transactions'); // Switch to transaction tab for editing
  };

  // --- Annuler la modification ---
  const handleCancelEdit = () => {
    setEditingTransaction(null);
    setNewTransactionAmount('');
    setNewTransactionDescription('');
    setNewTransactionType('deposit');
    setTransferTargetAccountId('');
  };

  // --- Supprimer une transaction ---
  const handleDeleteTransaction = async (transactionId, accountId, type, amount) => {
    if (userRole !== 'admin') {
      setError('Vous n\'êtes pas autorisé à supprimer des transactions.');
      return;
    }
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette transaction ? Le solde du compte sera ajusté.")) return;

    try {
      const transactionRef = doc(db, 'bankTransactions', transactionId);
      const accountRef = doc(db, 'bankAccounts', accountId);

      const accountDoc = await getDoc(accountRef);
      if (!accountDoc.exists()) {
        setError('Compte bancaire introuvable pour ajustement.');
        return;
      }
      let currentBalance = accountDoc.data().balance;

      // Revert balance change
      if (type === 'deposit') {
        currentBalance -= amount;
      } else if (type === 'withdrawal') {
        currentBalance += amount;
      } else if (type === 'transfer') {
        currentBalance += amount; // Source gets amount back
        // For transfers, also try to find and delete the corresponding target transaction
        const originalTransaction = transactions.find(t => t.id === transactionId);
        if (originalTransaction) {
            const targetDescriptionPrefix = `Virement reçu de ${bankAccounts.find(acc => acc.id === accountId)?.name || 'un autre compte'}`;
            const targetTransferQ = query(
                collection(db, 'bankTransactions'),
                where('timestamp', '==', originalTransaction.timestamp), // Weak, better to store ID
                where('amount', '==', amount),
                where('type', '==', 'deposit'),
                where('description', 'startsWith', targetDescriptionPrefix) // Use startsWith
            );
            const targetTransferSnapshot = await getDocs(targetTransferQ);
            if (!targetTransferSnapshot.empty) {
                const targetTxDoc = targetTransferSnapshot.docs[0];
                const targetTxAccountId = targetTxDoc.data().accountId;
                const targetAccountRef = doc(db, 'bankAccounts', targetTxAccountId);
                const targetAccountDoc = await getDoc(targetAccountRef);
                if (targetAccountDoc.exists()) {
                   await updateDoc(targetAccountRef, { balance: targetAccountDoc.data().balance - amount });
                   await deleteDoc(doc(db, 'bankTransactions', targetTxDoc.id));
                }
            }
        }
      }

      await updateDoc(accountRef, { balance: currentBalance });
      await deleteDoc(transactionRef);
      setMessage('Transaction supprimée et solde du compte ajusté !');
      setError('');
    } catch (error) {
      setError(`Erreur lors de la suppression de la transaction: ${error.message}`);
      setMessage('');
    }
  };

  // --- Logique de rapprochement bancaire ---
  const handleReconciliation = async (e) => {
    e.preventDefault();
    if (!reconciliationAccount || isNaN(parseFloat(bankStatementBalance)) || !reconciliationDate) {
      setError('Veuillez sélectionner un compte, le solde du relevé et la date de rapprochement.');
      return;
    }

    try {
      const accountRef = doc(db, 'bankAccounts', reconciliationAccount);
      const accountDoc = await getDoc(accountRef);
      if (!accountDoc.exists()) {
        setError('Compte bancaire introuvable pour le rapprochement.');
        return;
      }
      const systemBalance = accountDoc.data().balance;
      const statementBalance = parseFloat(bankStatementBalance);

      // Marquer les transactions non réconciliées comme réconciliées jusqu'à la date spécifiée
      const reconciliationCutoffDate = new Date(reconciliationDate);
      reconciliationCutoffDate.setHours(23, 59, 59, 999);

      const transactionsToReconcile = unreconciledTransactions.filter(t =>
        t.accountId === reconciliationAccount &&
        t.timestamp?.toDate() <= reconciliationCutoffDate &&
        !t.reconciled
      );

      const reconcilePromises = transactionsToReconcile.map(t =>
        updateDoc(doc(db, 'bankTransactions', t.id), {
          reconciled: true,
          reconciliationDate: serverTimestamp() // Marquer la date de rapprochement
        })
      );
      await Promise.all(reconcilePromises);

      // Enregistrer le rapport de rapprochement (pour l'historique)
      await addDoc(collection(db, 'bankReconciliations'), {
        schoolId: schoolId,
        accountId: reconciliationAccount,
        reconciliationDate: new Date(reconciliationDate),
        systemBalanceAtReconciliation: systemBalance,
        bankStatementBalance: statementBalance,
        difference: statementBalance - systemBalance,
        numTransactionsReconciled: transactionsToReconcile.length,
        createdAt: serverTimestamp(),
      });

      setMessage('Rapprochement bancaire effectué avec succès !');
      setReconciliationAccount('');
      setBankStatementBalance('');
      setReconciliationDate('');
      setError('');
    } catch (error) {
      setError(`Erreur lors du rapprochement bancaire: ${error.message}`);
      setMessage('');
    }
  };


  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Gestion Bancaire</h2>
      <p className="text-gray-700 mb-4">
        Bienvenue dans la section de gestion bancaire de votre école.
        <br />
        Votre rôle: <span className="font-semibold text-blue-600">{userRole}</span>
        {schoolId && <span className="font-semibold text-blue-600"> pour l'école ID: {schoolId}</span>}.
      </p>

      {/* --- Onglets de navigation --- */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('accounts')}
            className={`${activeTab === 'accounts' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm focus:outline-none`}
          >
            Comptes Bancaires
          </button>
          <button
            onClick={() => setActiveTab('transactions')}
            className={`${activeTab === 'transactions' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm focus:outline-none`}
          >
            Transactions
          </button>
          <button
            onClick={() => setActiveTab('reconciliation')}
            className={`${activeTab === 'reconciliation' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm focus:outline-none`}
          >
            Rapprochement Bancaire
          </button>
        </nav>
      </div>

      {/* --- Contenu de l'onglet "Comptes Bancaires" --- */}
      {activeTab === 'accounts' && (
        <div>
          <h3 className="text-xl font-semibold text-gray-700 mb-3">Gestion des Comptes Bancaires</h3>

          {/* Formulaire d'ajout de compte */}
          {userRole === 'admin' && (
            <form onSubmit={handleAddAccount} className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <h4 className="text-lg font-medium mb-3">Ajouter un nouveau compte</h4>
              <div className="flex flex-col md:flex-row gap-4">
                <input
                  type="text"
                  placeholder="Nom du compte (ex: Compte Courant, Caisse)"
                  value={newAccountName}
                  onChange={(e) => setNewAccountName(e.target.value)}
                  className="flex-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <input
                  type="number"
                  placeholder="Solde initial"
                  value={newAccountBalance}
                  onChange={(e) => setNewAccountBalance(e.target.value)}
                  className="flex-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  step="0.01"
                  required
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Ajouter Compte
                </button>
              </div>
            </form>
          )}

          {/* Liste des comptes bancaires */}
          {isLoadingAccounts ? (
            <p className="text-gray-600 italic">Chargement des comptes...</p>
          ) : bankAccounts.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                <thead>
                  <tr className="bg-gray-100 text-left text-gray-600 uppercase text-sm leading-normal">
                    <th className="py-3 px-6 text-left">Nom du Compte</th>
                    <th className="py-3 px-6 text-left">Solde Actuel</th>
                    <th className="py-3 px-6 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700 text-sm">
                  {bankAccounts.map((account) => (
                    <tr key={account.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-3 px-6 text-left whitespace-nowrap">
                        <span
                          className={`cursor-pointer hover:text-blue-600 ${selectedAccountId === account.id ? 'font-bold text-blue-600' : ''}`}
                          onClick={() => setSelectedAccountId(account.id)}
                        >
                          {account.name}
                        </span>
                      </td>
                      <td className="py-3 px-6 text-left">
                        <span className={`font-semibold ${account.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {account.balance.toFixed(2)} FCFA
                        </span>
                      </td>
                      <td className="py-3 px-6 text-center">
                        {userRole === 'admin' && (
                          <button
                            onClick={() => handleDeleteAccount(account.id)}
                            className="ml-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                            title="Supprimer le compte"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-600 italic">Aucun compte bancaire enregistré pour le moment. {userRole === 'admin' && 'Utilisez le formulaire ci-dessus pour en ajouter un.'}</p>
          )}
        </div>
      )}

      {/* --- Contenu de l'onglet "Transactions" --- */}
      {activeTab === 'transactions' && (
        <div>
          <h3 className="text-xl font-semibold text-gray-700 mb-3">{editingTransaction ? 'Modifier la Transaction' : 'Enregistrer une Nouvelle Transaction'}</h3>

          <form onSubmit={handleAddEditTransaction} className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label htmlFor="transactionAccount" className="block text-sm font-medium text-gray-700 mb-1">Compte concerné:</label>
                <select
                  id="transactionAccount"
                  value={newTransactionAccountId}
                  onChange={(e) => {
                    setNewTransactionAccountId(e.target.value);
                    if (e.target.value) {
                      setSelectedAccountId(e.target.value);
                    }
                  }}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={!!editingTransaction} // Désactiver si en mode édition pour éviter complexité de changement de compte
                >
                  <option value="">Sélectionner un compte</option>
                  {bankAccounts.map(account => (
                    <option key={account.id} value={account.id}>{account.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="transactionType" className="block text-sm font-medium text-gray-700 mb-1">Type de transaction:</label>
                <select
                  id="transactionType"
                  value={newTransactionType}
                  onChange={(e) => setNewTransactionType(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="deposit">Dépôt</option>
                  <option value="withdrawal">Retrait</option>
                  <option value="transfer">Virement</option>
                </select>
              </div>

              {newTransactionType === 'transfer' && (
                <div>
                  <label htmlFor="transferTargetAccount" className="block text-sm font-medium text-gray-700 mb-1">Compte cible du virement:</label>
                  <select
                    id="transferTargetAccount"
                    value={transferTargetAccountId}
                    onChange={(e) => setTransferTargetAccountId(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Sélectionner un compte cible</option>
                    {bankAccounts
                      .filter(account => account.id !== newTransactionAccountId)
                      .map(account => (
                        <option key={account.id} value={account.id}>{account.name}</option>
                      ))}
                  </select>
                </div>
              )}

              <div>
                <label htmlFor="transactionAmount" className="block text-sm font-medium text-gray-700 mb-1">Montant:</label>
                <input
                  id="transactionAmount"
                  type="number"
                  placeholder="Montant"
                  value={newTransactionAmount}
                  onChange={(e) => setNewTransactionAmount(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  step="0.01"
                  required
                />
              </div>

              <div className="md:col-span-full">
                <label htmlFor="transactionDescription" className="block text-sm font-medium text-gray-700 mb-1">Description:</label>
                <textarea
                  id="transactionDescription"
                  placeholder="Description de la transaction (ex: Paiement fournisseur, Dons, Salaire)"
                  value={newTransactionDescription}
                  onChange={(e) => setNewTransactionDescription(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="2"
                ></textarea>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
                {editingTransaction && (
                    <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                    >
                        Annuler
                    </button>
                )}
                <button
                    type="submit"
                    className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                >
                    {editingTransaction ? 'Modifier Transaction' : 'Enregistrer Transaction'}
                </button>
            </div>
          </form>

          {/* --- Filtres et Recherche --- */}
          <div className="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
            <h4 className="text-lg font-medium mb-3">Rechercher et Filtrer les Transactions</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">Recherche par description:</label>
                <input
                  type="text"
                  id="search"
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label htmlFor="filterType" className="block text-sm font-medium text-gray-700 mb-1">Filtrer par type:</label>
                <select
                  id="filterType"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="all">Tous les types</option>
                  <option value="deposit">Dépôt</option>
                  <option value="withdrawal">Retrait</option>
                  <option value="transfer">Virement</option>
                </select>
              </div>
              <div>
                <label htmlFor="filterStartDate" className="block text-sm font-medium text-gray-700 mb-1">Date de début:</label>
                <input
                  type="date"
                  id="filterStartDate"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label htmlFor="filterEndDate" className="block text-sm font-medium text-gray-700 mb-1">Date de fin:</label>
                <input
                  type="date"
                  id="filterEndDate"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>
            <button
                onClick={() => {
                    setSearchTerm('');
                    setFilterType('all');
                    setFilterStartDate('');
                    setFilterEndDate('');
                }}
                className="mt-4 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
                Réinitialiser Filtres
            </button>
          </div>

          {/* Historique des transactions du compte sélectionné */}
          <h4 className="text-lg font-medium mb-3">Historique des Transactions ({selectedAccountId ? bankAccounts.find(acc => acc.id === selectedAccountId)?.name : 'Sélectionner un compte'})</h4>
          {isLoadingTransactions ? (
            <p className="text-gray-600 italic">Chargement des transactions...</p>
          ) : selectedAccountId ? (
            transactions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                  <thead>
                    <tr className="bg-gray-100 text-left text-gray-600 uppercase text-sm leading-normal">
                      <th className="py-3 px-6 text-left">Date</th>
                      <th className="py-3 px-6 text-left">Type</th>
                      <th className="py-3 px-6 text-left">Montant</th>
                      <th className="py-3 px-6 text-left">Description</th>
                      <th className="py-3 px-6 text-center">Rapproché</th>
                      <th className="py-3 px-6 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700 text-sm">
                    {transactions.map((transaction) => (
                      <tr key={transaction.id} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="py-3 px-6 text-left">
                          {transaction.timestamp?.toDate().toLocaleDateString('fr-FR', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) || 'N/A'}
                        </td>
                        <td className="py-3 px-6 text-left capitalize">
                          <span className={`font-semibold ${
                            transaction.type === 'deposit' ? 'text-green-600' :
                            transaction.type === 'withdrawal' ? 'text-red-600' :
                            'text-blue-600'
                          }`}>
                            {transaction.type === 'deposit' ? 'Dépôt' :
                             transaction.type === 'withdrawal' ? 'Retrait' :
                             'Virement'}
                          </span>
                        </td>
                        <td className="py-3 px-6 text-left">
                          <span className={`font-semibold ${
                            transaction.type === 'deposit' ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {transaction.type === 'deposit' ? '+' : '-'} {transaction.amount.toFixed(2)} FCFA
                          </span>
                        </td>
                        <td className="py-3 px-6 text-left">{transaction.description || 'Aucune description'}</td>
                        <td className="py-3 px-6 text-center">
                            {transaction.reconciled ? (
                                <span className="text-green-500" title={`Rapproché le ${transaction.reconciliationDate?.toDate().toLocaleDateString()}`}>✓</span>
                            ) : (
                                <span className="text-gray-400">✗</span>
                            )}
                        </td>
                        <td className="py-3 px-6 text-center whitespace-nowrap">
                          {userRole === 'admin' && (
                            <>
                              <button
                                onClick={() => handleEditClick(transaction)}
                                className="p-1 text-blue-600 rounded-full hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                title="Modifier la transaction"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path></svg>
                              </button>
                              <button
                                onClick={() => handleDeleteTransaction(transaction.id, transaction.accountId, transaction.type, transaction.amount)}
                                className="ml-2 p-1 text-red-600 rounded-full hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                                title="Supprimer la transaction"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-600 italic">Aucune transaction trouvée pour ce compte avec les filtres actuels.</p>
            )
          ) : (
            <p className="text-gray-600 italic">Veuillez sélectionner un compte bancaire pour voir son historique de transactions, ou enregistrez une transaction pour un compte.</p>
          )}
        </div>
      )}

      {/* --- Contenu de l'onglet "Rapprochement Bancaire" --- */}
      {activeTab === 'reconciliation' && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h3 className="text-xl font-semibold text-gray-700 mb-3">Rapprochement Bancaire</h3>
          <p className="text-yellow-800 mb-4">
            Le rapprochement bancaire vous permet de comparer le solde de votre compte en système avec le solde de votre relevé bancaire.
            Les transactions non encore rapprochées sont listées ci-dessous.
          </p>

          <form onSubmit={handleReconciliation} className="mb-6 p-4 border border-gray-200 rounded-lg bg-yellow-100">
            <h4 className="text-lg font-medium mb-3">Effectuer un Rapprochement</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="reconciliationAccount" className="block text-sm font-medium text-gray-700 mb-1">Compte à rapprocher:</label>
                <select
                  id="reconciliationAccount"
                  value={reconciliationAccount}
                  onChange={(e) => {
                    setReconciliationAccount(e.target.value);
                    setSelectedAccountId(e.target.value); // Set selected account for transactions fetch
                  }}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Sélectionner un compte</option>
                  {bankAccounts.map(account => (
                    <option key={account.id} value={account.id}>{account.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="bankStatementBalance" className="block text-sm font-medium text-gray-700 mb-1">Solde du relevé bancaire:</label>
                <input
                  type="number"
                  id="bankStatementBalance"
                  placeholder="Solde relevé"
                  value={bankStatementBalance}
                  onChange={(e) => setBankStatementBalance(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  step="0.01"
                  required
                />
              </div>
              <div>
                <label htmlFor="reconciliationDate" className="block text-sm font-medium text-gray-700 mb-1">Date du rapprochement:</label>
                <input
                  type="date"
                  id="reconciliationDate"
                  value={reconciliationDate}
                  onChange={(e) => setReconciliationDate(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
            <p className="text-gray-700 mb-2">Solde du compte en système (actuel): <span className="font-semibold text-blue-700">{reconciliationAccount ? (bankAccounts.find(acc => acc.id === reconciliationAccount)?.balance || 0).toFixed(2) : '0.00'} FCFA</span></p>
            <p className="text-gray-700 mb-4">
                Différence: <span className={`font-semibold ${
                    (parseFloat(bankStatementBalance) - (bankAccounts.find(acc => acc.id === reconciliationAccount)?.balance || 0)).toFixed(2) == 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                    {(parseFloat(bankStatementBalance) - (bankAccounts.find(acc => acc.id === reconciliationAccount)?.balance || 0)).toFixed(2)} FCFA
                </span>
            </p>
            <button
              type="submit"
              className="px-6 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
              disabled={!reconciliationAccount || isNaN(parseFloat(bankStatementBalance)) || !reconciliationDate}
            >
              Exécuter Rapprochement
            </button>
          </form>

          <h4 className="text-lg font-medium mb-3">Transactions Non Rapprochées pour {reconciliationAccount ? bankAccounts.find(acc => acc.id === reconciliationAccount)?.name : 'le compte sélectionné'}</h4>
          {isLoadingTransactions ? (
            <p className="text-gray-600 italic">Chargement des transactions non rapprochées...</p>
          ) : reconciliationAccount && unreconciledTransactions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                <thead>
                  <tr className="bg-gray-100 text-left text-gray-600 uppercase text-sm leading-normal">
                    <th className="py-3 px-6 text-left">Date</th>
                    <th className="py-3 px-6 text-left">Type</th>
                    <th className="py-3 px-6 text-left">Montant</th>
                    <th className="py-3 px-6 text-left">Description</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700 text-sm">
                  {unreconciledTransactions.map((transaction) => (
                    <tr key={transaction.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-3 px-6 text-left">
                        {transaction.timestamp?.toDate().toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) || 'N/A'}
                      </td>
                      <td className="py-3 px-6 text-left capitalize">
                        <span className={`font-semibold ${
                          transaction.type === 'deposit' ? 'text-green-600' :
                          transaction.type === 'withdrawal' ? 'text-red-600' :
                          'text-blue-600'
                        }`}>
                          {transaction.type === 'deposit' ? 'Dépôt' :
                           transaction.type === 'withdrawal' ? 'Retrait' :
                           'Virement'}
                        </span>
                      </td>
                      <td className="py-3 px-6 text-left">
                        <span className={`font-semibold ${
                          transaction.type === 'deposit' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {transaction.type === 'deposit' ? '+' : '-'} {transaction.amount.toFixed(2)} FCFA
                        </span>
                      </td>
                      <td className="py-3 px-6 text-left">{transaction.description || 'Aucune description'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-600 italic">{reconciliationAccount ? 'Toutes les transactions pour ce compte sont rapprochées, ou aucune transaction trouvée.' : 'Veuillez sélectionner un compte pour voir les transactions non rapprochées.'}</p>
          )}
        </div>
      )}

      <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-blue-800 font-medium">
          Note: Cette version inclut des fonctionnalités avancées pour une application réelle. Pour une solution de production complète, les points suivants sont essentiels :
        </p>
        <ul className="list-disc list-inside text-blue-700 text-sm mt-2">
          <li>Des **règles de sécurité Firebase (Firestore Security Rules)** robustes pour gérer l'accès et les permissions de manière sécurisée côté serveur.</li>
          <li>Une **gestion avancée des erreurs** et des retours utilisateurs (ex: librairies de toasts/notifications).</li>
          <li>Pour les virements, stocker l'ID de la transaction liée pour simplifier la modification/suppression des deux côtés du virement.</li>
          <li>Implémentation de la **pagination** pour les listes de transactions très longues afin d'améliorer les performances.</li>
          <li>Des **rapports financiers** plus détaillés (bilan, compte de résultat simplifié, etc.).</li>
          <li>La possibilité d'**éditer les informations d'un compte bancaire** existant (nom, etc.).</li>
          <li>**Tests unitaires et d'intégration** pour garantir la fiabilité des opérations financières.</li>
        </ul>
      </div>
    </div>
  );
};

export default BankingManagement;